import { Injectable } from '@angular/core';
import { VendorOrderFile } from '../models/vendor-order.model';

/**
 * Service to compute file cancellation eligibility
 * Implements type-specific and upload-field blocking rules per TLS-2237
 *
 * Eligibility Rules:
 * - Non-DTP (isDtpFileRevised = false): not cancellable when originalName differs from uploadName
 * - DTP (isDtpFileRevised = true): cancellable only when translatedFileUploadName is empty
 * - Completed files are never cancellable
 */
@Injectable({
  providedIn: 'root',
})
export class FileEligibilityService {
  /**
   * Determine if a file is eligible for cancellation
   * @param file The file to check
   * @returns true if the file can be cancelled, false otherwise
   */
  isFileCancellable(file: VendorOrderFile): boolean {
    if (!file) {
      return false;
    }

    // Completed or already-cancelled files are never cancellable
    if (file.status === 'COMPLETED' || file.status === 'CANCELLED') {
      return false;
    }

    // Apply type-specific upload field blocking rules
    const isDtp = file.isDtpFileRevised === true;

    if (isDtp) {
      // DTP file: not cancellable if translatedFileUploadName is populated
      return !file.translatedFileUploadName;
    } else {
      // Non-DTP file: if originalName and uploadName differ, translation is already finished in XTM
      const originalName = (file.originalName ?? '').trim();
      const uploadName = (file.uploadName ?? '').trim();

      if (originalName && uploadName && originalName !== uploadName) {
        return false;
      }

      return true;
    }
  }

  /**
   * Get explanation for why a file is not cancellable
   * @param file The file to check
   * @returns Human-readable reason, or empty string if cancellable
   */
  getIneligibilityReason(file: VendorOrderFile): string {
    if (!file) {
      return '';
    }

    if (file.status === 'COMPLETED') {
      return 'This file has completed translation and cannot be cancelled.';
    }

    if (file.status === 'CANCELLED') {
      return 'This file has already been cancelled.';
    }

    const isDtp = file.isDtpFileRevised === true;
    if (isDtp && file.translatedFileUploadName) {
      return 'This DTP file has an uploaded translation and cannot be cancelled.';
    }

    if (!isDtp) {
      const originalName = (file.originalName ?? '').trim();
      const uploadName = (file.uploadName ?? '').trim();
      if (originalName && uploadName && originalName !== uploadName) {
        return 'This file has already completed translation in XTM and cannot be cancelled.';
      }
    }

    return '';
  }
}
